// utils/pdfGenerator.js
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('fr-FR');
};

// Helper function to format datetime
const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('fr-FR');
};

export const generatePatientReport = (patientData, consultations, vitals, treatments, medicalHistory) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPosition = 20;

  // En-tête avec logo et nom de la plateforme
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.text('AFDT-SUIVIMED', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 8;
  
  doc.setFontSize(12);
  doc.setFont(undefined, 'normal');
  doc.text('Plateforme Médicale', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 10;
  
  // Ligne de séparation
  doc.setLineWidth(0.5);
  doc.line(15, yPosition, pageWidth - 15, yPosition);
  yPosition += 10;
  
  // Titre du document
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text('DOSSIER MÉDICAL', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 15;

  // Date de génération
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.text(`Généré le: ${formatDateTime(new Date().toISOString())}`, pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 20;

  // Informations patient
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('INFORMATIONS PATIENT', 15, yPosition);
  yPosition += 10;

  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  const patientInfo = [
    [`Nom:`, `${patientData.lastName || 'N/A'}`],
    [`Prénom:`, `${patientData.firstName || 'N/A'}`],
    [`Date de naissance:`, `${formatDate(patientData.dateOfBirth)}`],
    [`Âge:`, `${patientData.dateOfBirth ? Math.floor((new Date() - new Date(patientData.dateOfBirth)) / (365.25 * 24 * 60 * 60 * 1000)) : 'N/A'} ans`],
    [`Sexe:`, `${patientData.gender || 'N/A'}`],
    [`Email:`, `${patientData.email || 'N/A'}`],
    [`Téléphone:`, `${patientData.phone || 'N/A'}`],
    [`Adresse:`, `${patientData.address || 'N/A'}`],
    [`Contact d'urgence:`, `${patientData.emergencyContact || 'N/A'}`],
    [`Téléphone d'urgence:`, `${patientData.emergencyPhone || 'N/A'}`],
    [`Numéro de dossier:`, `${patientData.fileNumber || 'N/A'}`]
  ];

  patientInfo.forEach(([label, value]) => {
    doc.text(label, 15, yPosition);
    doc.text(value, 60, yPosition);
    yPosition += 6;
  });

  yPosition += 10;

  // Consultations
  if (consultations && consultations.length > 0) {
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('CONSULTATIONS', 15, yPosition);
    yPosition += 10;

    const consultationsData = consultations
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .map(consultation => [
        formatDateTime(consultation.consultation_date),
        consultation.reason || 'N/A',
        consultation.diagnosis || 'N/A',
        consultation.notes || 'N/A'
      ]);

    doc.autoTable({
      startY: yPosition,
      head: [['Date', 'Motif', 'Diagnostic', 'Notes']],
      body: consultationsData,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [66, 139, 202] },
      margin: { left: 15, right: 15 }
    });

    yPosition = doc.lastAutoTable.finalY + 15;
  }

  // Constantes vitales
  if (vitals && vitals.length > 0) {
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('CONSTANTES VITALES', 15, yPosition);
    yPosition += 10;

    const vitalsData = vitals
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .map(vital => [
        formatDateTime(vital.measurement_date),
        vital.systolic_pressure ? `${vital.systolic_pressure}/${vital.diastolic_pressure}` : 'N/A',
        vital.heart_rate || 'N/A',
        vital.temperature || 'N/A',
        vital.weight || 'N/A',
        vital.height || 'N/A'
      ]);

    doc.autoTable({
      startY: yPosition,
      head: [['Date', 'Tension', 'FC', 'Temp', 'Poids', 'Taille']],
      body: vitalsData,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [66, 139, 202] },
      margin: { left: 15, right: 15 }
    });

    yPosition = doc.lastAutoTable.finalY + 15;
  }

  // Traitements
  if (treatments && treatments.length > 0) {
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('TRAITEMENTS', 15, yPosition);
    yPosition += 10;

    const treatmentsData = treatments
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .map(treatment => [
        treatment.medication_name || 'N/A',
        treatment.dosage || 'N/A',
        treatment.frequency || 'N/A',
        formatDate(treatment.start_date),
        formatDate(treatment.end_date),
        treatment.notes || 'N/A'
      ]);

    doc.autoTable({
      startY: yPosition,
      head: [['Médicament', 'Dosage', 'Fréquence', 'Début', 'Fin', 'Notes']],
      body: treatmentsData,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [66, 139, 202] },
      margin: { left: 15, right: 15 }
    });

    yPosition = doc.lastAutoTable.finalY + 15;
  }

  // Antécédents médicaux
  if (medicalHistory && medicalHistory.length > 0) {
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('ANTÉCÉDENTS MÉDICAUX', 15, yPosition);
    yPosition += 10;

    const historyData = medicalHistory
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .map(history => [
        history.history_type || 'N/A',
        history.title || 'N/A',
        formatDate(history.start_date),
        formatDate(history.end_date),
        history.description || 'N/A'
      ]);

    doc.autoTable({
      startY: yPosition,
      head: [['Type', 'Titre', 'Début', 'Fin', 'Description']],
      body: historyData,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [66, 139, 202] },
      margin: { left: 15, right: 15 }
    });
  }

  // Pied de page
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(`Page ${i} sur ${pageCount}`, pageWidth - 30, doc.internal.pageSize.getHeight() - 10);
  }

  // Sauvegarder le fichier
  const fileName = `dossier-${patientData.lastName || 'patient'}-${patientData.firstName || ''}-${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};